import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { rememberEmbedReturnPage } from '~/lib/embed';

const AUTH_STORE_NAME = 'customer-auth-store';

// Customer data returned from login
export type CustomerData = {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
};

// Auth state interface
export type CustomerAuthState = {
    // Auth state
    token: string | null;
    customer: CustomerData | null;

    // Return URL for login flow - where to redirect after login
    returnUrl: string | null;

    // Actions
    login: (token: string, customer: CustomerData) => void;
    logout: () => void;
    setReturnUrl: (url: string) => void;
    clearReturnUrl: () => void;
    startLogin: (returnUrl: string) => void;

    // Computed helper
    isAuthenticated: () => boolean;
};

// Create the customer auth store with persistence
export const useCustomerAuthStore = create<CustomerAuthState>()(
    persist(
        (set, get) => ({
            token: null,
            customer: null,
            returnUrl: null,

            login: (token: string, customer: CustomerData) => {
                // Also store token in localStorage directly for tRPC client compatibility
                localStorage.setItem('token', token);
                set({ token, customer });
            },

            logout: () => {
                // Remove token from localStorage for tRPC client
                localStorage.removeItem('token');
                set({ token: null, customer: null });
            },

            setReturnUrl: (url: string) => {
                set({ returnUrl: url });
            },

            clearReturnUrl: () => {
                set({ returnUrl: null });
            },

            startLogin: (returnUrl: string) => {
                rememberEmbedReturnPage();
                set({ returnUrl });
            },

            isAuthenticated: () => {
                const state = get();
                return !!state.token && !!state.customer;
            },
        }),
        {
            name: AUTH_STORE_NAME,
            storage: createJSONStorage(() => localStorage),
            // Persist token, customer, and returnUrl
            partialize: (state) => ({
                token: state.token,
                customer: state.customer,
                returnUrl: state.returnUrl,
            }),
            onRehydrateStorage: () => (state) => {
                // Sync token to localStorage for tRPC client on hydration
                if (state?.token) {
                    localStorage.setItem('token', state.token);
                }
            },
        }
    )
);

// Without this, a tab with stale in-memory state (e.g. the drawer that requested the magic link)
// would overwrite a login completed in another tab on its next state change.
window.addEventListener('storage', (event) => {
    if (event.key === AUTH_STORE_NAME) {
        useCustomerAuthStore.persist.rehydrate();
    }
});
